// Auto-generated by scripts/split_calculators.py — keeps deterministic oilfield formulas in per-category modules.
import { round, num, positive, toRad } from "./_common";
// eslint-disable-next-line no-unused-vars
const _imports_ok = { round, num, positive, toRad };

// ===== Category: wellcontrol =====
// --- Well Control ---
// =========================================================================
// WELL CONTROL
// =========================================================================

export function killMudWeight({ sidpp, tvd, currentMW }) {
  const P = num(sidpp, "SIDPP"); if (P < 0) throw new Error("SIDPP cannot be negative");
  const D = positive(num(tvd, "TVD"), "TVD");
  const M = positive(num(currentMW, "Current MW"), "Current MW");
  const kmw = P / (0.052 * D) + M;
  return {
    result: round(kmw, 2),
    unit: "ppg",
    formula: "KMW = SIDPP / (0.052 × TVD) + OMW",
    steps: [
      `SIDPP=${P} psi, TVD=${D} ft, OMW=${M} ppg`,
      `KMW = ${P} / (0.052 × ${D}) + ${M}`,
      `KMW = ${round(P / (0.052 * D), 3)} + ${M}`,
      `KMW = ${round(kmw, 2)} ppg`,
    ],
    inputs: { "SIDPP (psi)": P, "TVD (ft)": D, "Current MW (ppg)": M },
  };
}

export function initialCirculatingPressure({ sidpp, scr }) {
  const P = num(sidpp, "SIDPP"); if (P < 0) throw new Error("SIDPP cannot be negative");
  const S = positive(num(scr, "Slow Pump Pressure (SCR)"), "Slow Pump Pressure");
  const icp = P + S;
  return {
    result: round(icp, 0),
    unit: "psi",
    formula: "ICP = SIDPP + SCR",
    steps: [`ICP = ${P} + ${S} = ${round(icp, 0)} psi`],
    inputs: { "SIDPP (psi)": P, "SCR (psi)": S },
  };
}

export function finalCirculatingPressure({ scr, kmw, omw }) {
  const S = positive(num(scr, "SCR"), "SCR");
  const K = positive(num(kmw, "KMW"), "KMW");
  const O = positive(num(omw, "OMW"), "OMW");
  const fcp = S * (K / O);
  return {
    result: round(fcp, 0),
    unit: "psi",
    formula: "FCP = SCR × (KMW / OMW)",
    steps: [
      `SCR=${S} psi, KMW=${K} ppg, OMW=${O} ppg`,
      `FCP = ${S} × (${K} / ${O}) = ${round(fcp, 0)} psi`,
    ],
    inputs: { "SCR (psi)": S, "KMW (ppg)": K, "OMW (ppg)": O },
  };
}

export function formationPressureFromSIDPP({ sidpp, omw, tvd }) {
  const P = num(sidpp, "SIDPP"); if (P < 0) throw new Error("SIDPP cannot be negative");
  const M = positive(num(omw, "OMW"), "OMW");
  const D = positive(num(tvd, "TVD"), "TVD");
  const hp = 0.052 * M * D;
  const fp = P + hp;
  return {
    result: round(fp, 0),
    unit: "psi",
    formula: "Pform = SIDPP + (0.052 × OMW × TVD)",
    steps: [
      `Hydrostatic = 0.052 × ${M} × ${D} = ${round(hp, 0)} psi`,
      `Pform = ${P} + ${round(hp, 0)} = ${round(fp, 0)} psi`,
    ],
    inputs: { "SIDPP (psi)": P, "OMW (ppg)": M, "TVD (ft)": D },
  };
}

export function shutInDataSheet({ sidpp, sicp, pitGain, tvd, omw }) {
  const P = num(sidpp, "SIDPP"); if (P < 0) throw new Error("SIDPP < 0");
  const C = num(sicp, "SICP"); if (C < 0) throw new Error("SICP < 0");
  const G = num(pitGain, "Pit Gain"); if (G < 0) throw new Error("Pit Gain < 0");
  const D = positive(num(tvd, "TVD"), "TVD");
  const M = positive(num(omw, "OMW"), "OMW");
  const kmw = P / (0.052 * D) + M;
  const formPress = P + 0.052 * M * D;
  const influxGradient = C >= P ? (M - (C - P) / (0.052 * Math.max(G, 1))) : M; // rough; G in ft column
  return {
    result: round(kmw, 2),
    unit: "ppg (KMW)",
    formula: "SIDPP, SICP, Pit Gain → KMW, Formation Pressure",
    steps: [
      `SIDPP=${P} psi, SICP=${C} psi, Pit Gain=${G} bbl, TVD=${D} ft, OMW=${M} ppg`,
      `Kill Mud Weight = ${P}/(0.052×${D}) + ${M} = ${round(kmw, 2)} ppg`,
      `Formation Pressure = ${P} + 0.052×${M}×${D} = ${round(formPress, 0)} psi`,
      `(Approx influx gradient indication: ${round(influxGradient, 2)} ppg-eq.)`,
    ],
    inputs: { "SIDPP": P, "SICP": C, "Pit Gain (bbl)": G, "TVD (ft)": D, "OMW (ppg)": M },
    extras: {
      "Formation Pressure (psi)": round(formPress, 0),
      "Kill Mud Weight (ppg)": round(kmw, 2),
    },
  };
}

// --- v1.4 — Well Control additions ---
// ----- Well Control additions -----

export function pitGainConversion({ direction, value }) {
  const d = (direction || "gal_to_bbl").toLowerCase();
  const v = positive(num(value, "Value"), "Value");
  let out, unit, formula, steps;
  if (d === "gal_to_bbl") {
    out = v / 42;
    unit = "bbl";
    formula = "bbl = gal / 42";
    steps = [`${v} gal / 42 = ${round(out, 3)} bbl`];
  } else {
    out = v * 42;
    unit = "gal";
    formula = "gal = bbl × 42";
    steps = [`${v} bbl × 42 = ${round(out, 1)} gal`];
  }
  return {
    result: round(out, 3),
    unit, formula, steps,
    inputs: { Direction: d, Value: v },
  };
}

export function tripMargin({ tripMarginPpg, tvd }) {
  const M = positive(num(tripMarginPpg, "Trip Margin"), "Trip Margin");
  const D = positive(num(tvd, "TVD"), "TVD");
  const p = 0.052 * M * D;
  return {
    result: round(p, 1),
    unit: "psi",
    formula: "Trip Margin (psi) = 0.052 × Trip Margin (ppg) × TVD",
    steps: [`= 0.052 × ${M} × ${D} = ${round(p, 1)} psi`],
    inputs: { "Trip Margin (ppg)": M, "TVD (ft)": D },
  };
}

export function maaspWorksheet({ leakoffPressure, mwAtTest, currentMW, shoeTVD, safetyMargin }) {
  const P_lot = positive(num(leakoffPressure, "LOT/FIT Pressure"), "LOT/FIT Pressure");
  const Wt = positive(num(mwAtTest, "Mud Weight at Test"), "Mud Weight at Test");
  const Wc = positive(num(currentMW, "Current Mud Weight"), "Current Mud Weight");
  const D = positive(num(shoeTVD, "Shoe TVD"), "Shoe TVD");
  const S = num(safetyMargin || 0, "Safety Margin");
  if (S < 0) throw new Error("Safety margin must be ≥ 0");
  // EMW at LOT (ppg-equiv) = (P_lot / (0.052 × D)) + Wt
  const emw = P_lot / (0.052 * D) + Wt;
  // MAASP support = (EMW − Current MW) × 0.052 × TVD − safety_psi
  const safetyPsi = 0.052 * S * D;
  const maasp = (emw - Wc) * 0.052 * D - safetyPsi;
  return {
    result: round(maasp, 0),
    unit: "psi (worksheet)",
    formula: "EMW = P_LOT/(0.052·D) + MW_test ;  MAASP_support ≈ (EMW − MW_current)·0.052·D − safety",
    steps: [
      `EMW at shoe = ${P_lot}/(0.052×${D}) + ${Wt} = ${round(emw, 3)} ppg-eq`,
      `Safety = 0.052 × ${S} × ${D} = ${round(safetyPsi, 0)} psi`,
      `MAASP support = (${round(emw, 3)} − ${Wc}) × 0.052 × ${D} − ${round(safetyPsi, 0)} = ${round(maasp, 0)} psi`,
    ],
    inputs: { "LOT/FIT (psi)": P_lot, "MW at Test (ppg)": Wt, "Current MW (ppg)": Wc, "Shoe TVD (ft)": D, "Safety Margin (ppg)": S },
    extras: { "EMW at Shoe (ppg-eq)": round(emw, 3), "Safety (psi)": round(safetyPsi, 0) },
    worksheetWarning: true,
  };
}

export function pumpPressureSchedule({ icp, fcp, strokesToBit, intervalStrokes }) {
  const I = positive(num(icp, "ICP"), "ICP");
  const F = positive(num(fcp, "FCP"), "FCP");
  const N = positive(num(strokesToBit, "Strokes to Bit"), "Strokes to Bit");
  const step = positive(num(intervalStrokes || 100, "Interval"), "Interval");
  const slope = (F - I) / N;
  const lines = [];
  for (let s = 0; s <= N; s += step) {
    const p = I + slope * s;
    lines.push(`@ stk ${Math.round(s).toString().padStart(5, " ")}  →  ${Math.round(p)} psi`);
  }
  if (lines[lines.length - 1] && !lines[lines.length - 1].startsWith(`@ stk ${String(Math.round(N)).padStart(5, " ")}`)) {
    lines.push(`@ stk ${Math.round(N).toString().padStart(5, " ")}  →  ${Math.round(F)} psi`);
  }
  return {
    result: Math.round(slope * 100) / 100,
    unit: "psi/stroke (slope)",
    formula: "Linear interp from ICP at stroke 0 to FCP at stroke N",
    steps: [
      `ICP=${I} psi, FCP=${F} psi, N=${N} strokes, interval=${step}`,
      `Slope = (${F}−${I})/${N} = ${round(slope, 4)} psi/stroke`,
      "Draft schedule (for qualified review only):",
      ...lines,
    ],
    inputs: { ICP: I, FCP: F, "Strokes to Bit": N, "Interval (stk)": step },
    extras: { "Slope (psi/stk)": round(slope, 4) },
    worksheetWarning: true,
  };
}

export function wellKillWorksheet({ currentMW, sidpp, sicp, tvd, scr, pumpOutput, drillStringCap, annularVol, pitGain }) {
  const Wc = positive(num(currentMW, "Current MW"), "Current MW");
  const Pd = num(sidpp, "SIDPP"); if (Pd < 0) throw new Error("SIDPP < 0");
  const Pc = num(sicp, "SICP"); if (Pc < 0) throw new Error("SICP < 0");
  const D = positive(num(tvd, "TVD"), "TVD");
  const SCR = positive(num(scr, "SCR"), "SCR");
  const Q = positive(num(pumpOutput, "Pump Output"), "Pump Output");
  const Vds = positive(num(drillStringCap, "Drill String Cap"), "Drill String Cap");
  const Va = positive(num(annularVol, "Annular Vol"), "Annular Vol");
  const G = num(pitGain || 0, "Pit Gain"); if (G < 0) throw new Error("Pit gain < 0");

  const kmw = Pd / (0.052 * D) + Wc;
  const icp = Pd + SCR;
  const fcp = SCR * (kmw / Wc);
  const stkToBit = Math.round(Vds / Q);
  const buStk = Math.round(Va / Q);
  const totalStk = stkToBit + buStk;
  const totalTime = (totalStk * Q) / Q; // strokes -> minutes via Q (bbl/stk) needs flow rate min⁻¹
  // Circulation time in minutes = total volume / flow_rate; flow rate = strokes/min × Q.
  // Without spm, return total strokes only.
  return {
    result: round(kmw, 2),
    unit: "ppg (KMW)",
    formula: "Composite kill worksheet — KMW, ICP, FCP, strokes-to-bit, bottoms-up",
    steps: [
      `KMW = ${Pd}/(0.052×${D}) + ${Wc} = ${round(kmw, 2)} ppg`,
      `ICP = SIDPP + SCR = ${Pd} + ${SCR} = ${icp} psi`,
      `FCP = SCR × (KMW/OMW) = ${SCR} × (${round(kmw, 2)}/${Wc}) = ${round(fcp, 0)} psi`,
      `Strokes to bit = ${Vds}/${Q} = ${stkToBit} stk`,
      `Bottoms-up strokes = ${Va}/${Q} = ${buStk} stk`,
      `Total kill strokes = ${stkToBit + buStk} stk`,
      `Pit gain reported: ${G} bbl`,
    ],
    inputs: {
      "Current MW (ppg)": Wc, "SIDPP (psi)": Pd, "SICP (psi)": Pc, "TVD (ft)": D,
      "SCR (psi)": SCR, "Pump Output (bbl/stk)": Q, "Drill String Cap (bbl)": Vds,
      "Annular Vol (bbl)": Va, "Pit Gain (bbl)": G,
    },
    extras: {
      "KMW (ppg)": round(kmw, 2),
      "ICP (psi)": icp,
      "FCP (psi)": round(fcp, 0),
      "Strokes to Bit": stkToBit,
      "Bottoms-Up Strokes": buStk,
      "Total Kill Strokes": stkToBit + buStk,
    },
    worksheetWarning: true,
  };
}



// =========================================================================
// STAGE 7 — Hydrostatic / formation-pressure / trip-margin helpers.
// All entries that reference these are tagged `wellControl: true` in catalog.js,
// which triggers the red Well Control safety banner via CalculatorRunner.
// =========================================================================

// Hydrostatic Pressure at Shoe:  P_shoe (psi) = 0.052 × MW × Shoe TVD
export function hydrostaticAtShoe({ mw, shoeTvd }) {
  const M = positive(num(mw, "Mud Weight"), "Mud Weight");
  const D = positive(num(shoeTvd, "Shoe TVD"), "Shoe TVD");
  const p = 0.052 * M * D;
  return {
    result: round(p, 2),
    unit: "psi",
    formula: "P_shoe = 0.052 × MW × Shoe TVD",
    steps: [
      `MW = ${M} ppg,  Shoe TVD = ${D} ft`,
      `P_shoe = 0.052 × ${M} × ${D}`,
      `P_shoe = ${round(p, 2)} psi`,
    ],
    inputs: { "Mud Weight (ppg)": M, "Shoe TVD (ft)": D },
  };
}

// Formation Pressure from current MW + SIDPP:
// P_form (psi) = 0.052 × Current MW × TVD + SIDPP
export function formationPressureFromCurrentMW({ currentMW, tvd, sidpp }) {
  const M = positive(num(currentMW, "Current Mud Weight"), "Current Mud Weight");
  const D = positive(num(tvd, "TVD"), "TVD");
  const P = num(sidpp, "SIDPP"); if (P < 0) throw new Error("SIDPP cannot be negative");
  const hp = 0.052 * M * D;
  const pf = hp + P;
  return {
    result: round(pf, 2),
    unit: "psi",
    formula: "P_form = 0.052 × Current MW × TVD + SIDPP",
    steps: [
      `Hydrostatic = 0.052 × ${M} × ${D} = ${round(hp, 2)} psi`,
      `P_form = ${round(hp, 2)} + ${P} = ${round(pf, 2)} psi`,
    ],
    inputs: { "Current MW (ppg)": M, "TVD (ft)": D, "SIDPP (psi)": P },
    extras: { "Hydrostatic (psi)": round(hp, 2) },
  };
}

// Equivalent Mud Weight from Formation Pressure:
// EMW (ppg) = P_form / (0.052 × TVD)
export function emwFromFormationPressure({ formationPressure, tvd }) {
  const P = positive(num(formationPressure, "Formation Pressure"), "Formation Pressure");
  const D = positive(num(tvd, "TVD"), "TVD");
  const emw = P / (0.052 * D);
  return {
    result: round(emw, 2),
    unit: "ppg",
    formula: "EMW = P_form / (0.052 × TVD)",
    steps: [
      `P_form = ${P} psi,  TVD = ${D} ft`,
      `EMW = ${P} / (0.052 × ${D})`,
      `EMW = ${round(emw, 2)} ppg`,
    ],
    inputs: { "Formation Pressure (psi)": P, "TVD (ft)": D },
  };
}

// Trip Margin Pressure:  ΔP (psi) = 0.052 × Trip Margin (ppg) × TVD
export function tripMarginPressure({ tripMarginPpg, tvd }) {
  const M = positive(num(tripMarginPpg, "Trip Margin"), "Trip Margin");
  const D = positive(num(tvd, "TVD"), "TVD");
  const p = 0.052 * M * D;
  return {
    result: round(p, 2),
    unit: "psi",
    formula: "Trip Margin Pressure = 0.052 × Trip Margin (ppg) × TVD",
    steps: [
      `Trip Margin = ${M} ppg,  TVD = ${D} ft`,
      `ΔP = 0.052 × ${M} × ${D}`,
      `ΔP = ${round(p, 2)} psi`,
    ],
    inputs: { "Trip Margin (ppg)": M, "TVD (ft)": D },
  };
}

// Required Trip Margin Mud Weight:
// Required ΔMW (ppg) = Desired ΔP / (0.052 × TVD)
export function requiredTripMarginMW({ desiredPressure, tvd }) {
  const P = positive(num(desiredPressure, "Desired Trip Margin Pressure"), "Desired Trip Margin Pressure");
  const D = positive(num(tvd, "TVD"), "TVD");
  const dMW = P / (0.052 * D);
  return {
    result: round(dMW, 2),
    unit: "ppg",
    formula: "Required Trip Margin (ppg) = Desired ΔP / (0.052 × TVD)",
    steps: [
      `Desired ΔP = ${P} psi,  TVD = ${D} ft`,
      `ΔMW = ${P} / (0.052 × ${D})`,
      `ΔMW = ${round(dMW, 2)} ppg`,
    ],
    inputs: { "Desired ΔP (psi)": P, "TVD (ft)": D },
  };
}

// =========================================================================
// STAGE 8 — Shut-in pressure delta, pit-gain conversions, influx height,
// kill mud-weight increase.
// =========================================================================

// Shut-In Pressure Difference:  ΔP = SICP − SIDPP
export function shutInPressureDifference({ sicp, sidpp }) {
  const C = num(sicp, "SICP"); if (C < 0) throw new Error("SICP cannot be negative");
  const P = num(sidpp, "SIDPP"); if (P < 0) throw new Error("SIDPP cannot be negative");
  const d = C - P;
  return {
    result: round(d, 2),
    unit: "psi",
    formula: "ΔP = SICP − SIDPP",
    steps: [
      `SICP = ${C} psi,  SIDPP = ${P} psi`,
      `ΔP = ${C} − ${P} = ${round(d, 2)} psi`,
    ],
    inputs: { "SICP (psi)": C, "SIDPP (psi)": P },
  };
}

// Pit Gain Barrels from Gallons:  bbl = gal / 42
export function pitGainBblFromGal({ gallons }) {
  const g = positive(num(gallons, "Pit Gain (gal)"), "Pit Gain (gal)");
  const b = g / 42;
  return {
    result: round(b, 3),
    unit: "bbl",
    formula: "Pit Gain (bbl) = Pit Gain (gal) / 42",
    steps: [
      `Pit Gain = ${g} gal`,
      `bbl = ${g} / 42 = ${round(b, 3)} bbl`,
    ],
    inputs: { "Pit Gain (gal)": g },
  };
}

// Pit Gain Gallons from Barrels:  gal = bbl × 42
export function pitGainGalFromBbl({ barrels }) {
  const b = positive(num(barrels, "Pit Gain (bbl)"), "Pit Gain (bbl)");
  const g = b * 42;
  return {
    result: round(g, 2),
    unit: "gal",
    formula: "Pit Gain (gal) = Pit Gain (bbl) × 42",
    steps: [
      `Pit Gain = ${b} bbl`,
      `gal = ${b} × 42 = ${round(g, 2)} gal`,
    ],
    inputs: { "Pit Gain (bbl)": b },
  };
}

// Influx Height Estimate:  h (ft) = Pit Gain (bbl) / Annular Capacity (bbl/ft)
// Simplified volume-height estimate only. Flagged worksheetWarning so the
// red DRAFT banner is displayed alongside the well-control banner.
export function influxHeightEstimate({ pitGain, annularCapacity }) {
  const V = positive(num(pitGain, "Pit Gain"), "Pit Gain");
  const C = positive(num(annularCapacity, "Annular Capacity"), "Annular Capacity");
  const h = V / C;
  return {
    result: round(h, 2),
    unit: "ft",
    formula: "Influx Height = Pit Gain (bbl) / Annular Capacity (bbl/ft)",
    steps: [
      `Pit Gain = ${V} bbl,  Annular Capacity = ${C} bbl/ft`,
      `h = ${V} / ${C} = ${round(h, 2)} ft`,
    ],
    inputs: { "Pit Gain (bbl)": V, "Annular Capacity (bbl/ft)": C },
    extras: {
      "Note": "Simplified volume-height estimate only. Actual influx behavior depends on wellbore geometry, compressibility, gas expansion, flow path, temperature, pressure, and well conditions.",
    },
    worksheetWarning: true,
  };
}

// Kill Mud Weight Increase:  ΔMW (ppg) = SIDPP / (0.052 × TVD)
export function killMudWeightIncrease({ sidpp, tvd }) {
  const P = num(sidpp, "SIDPP"); if (P < 0) throw new Error("SIDPP cannot be negative");
  const D = positive(num(tvd, "TVD"), "TVD");
  const dMW = P / (0.052 * D);
  return {
    result: round(dMW, 2),
    unit: "ppg",
    formula: "ΔMW = SIDPP / (0.052 × TVD)",
    steps: [
      `SIDPP = ${P} psi,  TVD = ${D} ft`,
      `ΔMW = ${P} / (0.052 × ${D})`,
      `ΔMW = ${round(dMW, 2)} ppg`,
    ],
    inputs: { "SIDPP (psi)": P, "TVD (ft)": D },
  };
}


// =========================================================================
// KICK TOLERANCE / INFLUX SCENARIO SUPPORT (F1a)
// Two-mode support calculator. Documentation support only — no verdict.
// =========================================================================

export function kickToleranceSupport({
  mode, maasp, currentMW, shoeTVD, influxHeight, influxGradient,
  annularCapacityAtShoe, safetyMarginPsi,
}) {
  // Accept the long-form select labels emitted by the catalog dropdown
  // ("Mode A — Influx Scenario Pressure Margin" / "Mode B — Planning
  // Kick Tolerance Volume") AND the short codes ("A" / "B") used by the
  // direct unit tests. The mode-selection is the only behavior gate; the
  // formula logic below is unchanged.
  const raw = String(mode || "A").trim();
  const m = raw.toUpperCase().startsWith("MODE A") || raw.toUpperCase() === "A" ? "A"
          : raw.toUpperCase().startsWith("MODE B") || raw.toUpperCase() === "B" ? "B"
          : "";
  if (m !== "A" && m !== "B") throw new Error("Mode must be 'Mode A — Influx Scenario Pressure Margin' or 'Mode B — Planning Kick Tolerance Volume'");
  const P_maasp = positive(num(maasp, "MAASP"), "MAASP");
  const MW = positive(num(currentMW, "Current Mud Weight"), "Current Mud Weight");
  const Dshoe = positive(num(shoeTVD, "Shoe / Weak-Point TVD"), "Shoe / Weak-Point TVD");
  const Gi = positive(num(influxGradient, "Influx Gradient"), "Influx Gradient");
  const Pmax = P_maasp + 0.052 * MW * Dshoe;

  const baseDisclaimers = [
    "Documentation support only. Review and approval by competent well-control personnel is required before any well-control operation.",
    "This simplified support calculator does not determine whether operations may continue or whether a kick may be circulated. Final interpretation must be made by competent well-control personnel using authorized procedures and well-specific data.",
  ];

  if (m === "A") {
    const Hi = positive(num(influxHeight, "Influx Height"), "Influx Height");
    const Phydro = 0.052 * MW * Math.max(Dshoe - Hi, 0) + Gi * Hi;
    const margin = Pmax - Phydro;
    const capRaw = annularCapacityAtShoe;
    const haveCap = capRaw !== "" && capRaw !== null && capRaw !== undefined && !Number.isNaN(Number(capRaw)) && Number(capRaw) > 0;
    const Cap = haveCap ? Number(capRaw) : null;
    const scenarioVol = haveCap ? round(Hi * Cap, 2) : null;
    return {
      result: round(margin, 0),
      unit: "psi (Mode A — scenario margin)",
      formula: "Mode A: P_max_at_shoe = MAASP + 0.052·MW·TVD ;  P_hydro = 0.052·MW·(TVD−H) + G_influx·H ;  Margin = P_max − P_hydro",
      steps: [
        "Mode A — Influx Scenario Pressure Margin",
        `P_max_at_shoe = ${P_maasp} + 0.052 × ${MW} × ${Dshoe} = ${round(Pmax, 0)} psi`,
        `P_hydro = 0.052 × ${MW} × (${Dshoe} − ${Hi}) + ${Gi} × ${Hi} = ${round(Phydro, 0)} psi`,
        `Allowable_pressure_margin = ${round(Pmax, 0)} − ${round(Phydro, 0)} = ${round(margin, 0)} psi`,
        haveCap
          ? `Entered-scenario influx volume = ${Hi} × ${Cap} = ${scenarioVol} bbl`
          : "Entered-scenario influx volume — Not calculated; annular capacity at shoe not entered.",
        ...baseDisclaimers,
        "Assumptions/exclusions: vertical-well; single-bubble single-density influx; no gas expansion; no annular friction; subsea choke/kill-line friction not included (future enhancement).",
      ],
      outputs: {
        "Maximum allowable pressure at shoe (psi)": round(Pmax, 0),
        "Hydrostatic pressure at shoe with entered influx (psi)": round(Phydro, 0),
        "Allowable pressure margin (psi)": round(margin, 0),
        "Entered-scenario influx volume (bbl)": haveCap ? scenarioVol : "Not calculated — annular capacity at shoe not entered",
      },
      inputs: {
        "MAASP (psi)": P_maasp, "Current MW (ppg)": MW, "Shoe TVD (ft)": Dshoe,
        "Influx Height (ft)": Hi, "Influx Gradient (psi/ft)": Gi,
        "Annular Capacity at Shoe (bbl/ft)": haveCap ? Cap : "Not entered",
      },
    };
  }

  // Mode B
  const Cap = positive(num(annularCapacityAtShoe, "Annular Capacity at Shoe"), "Annular Capacity at Shoe");
  const S = (safetyMarginPsi === "" || safetyMarginPsi === null || safetyMarginPsi === undefined) ? 0 : num(safetyMarginPsi, "Safety Margin");
  if (S < 0) throw new Error("Safety margin must be ≥ 0");

  const modeBAssumption = "Assumptions/exclusions: vertical-well; single-bubble single-density influx; constant annular capacity at the shoe; no gas expansion; no deviated-well geometry; no annular friction; subsea choke/kill-line friction not included (future enhancement).";

  if (S >= P_maasp) {
    return {
      result: "Not calculated",
      unit: "(Mode B)",
      formula: "Mode B: H_max = (MAASP − Safety_margin_psi) / (0.052·MW − G_influx) ;  Volume = H_max · annular_capacity",
      steps: [
        "Mode B — Simplified Planning Kick Tolerance Volume",
        `Validation: Safety_margin_psi (${S}) ≥ MAASP (${P_maasp}).`,
        "Not calculated — safety margin is greater than or equal to MAASP. Verify inputs.",
        ...baseDisclaimers, modeBAssumption,
      ],
      outputs: {
        "Maximum allowable influx height (ft)": "Not calculated — safety margin is greater than or equal to MAASP. Verify inputs.",
        "Estimated kick tolerance volume (bbl)": "Not calculated",
        "Safety margin used (psi)": S,
      },
      inputs: { "MAASP (psi)": P_maasp, "Current MW (ppg)": MW, "Influx Gradient (psi/ft)": Gi, "Shoe TVD (ft)": Dshoe, "Annular Capacity (bbl/ft)": Cap, "Safety Margin (psi)": S },
    };
  }

  const denom = 0.052 * MW - Gi;
  if (denom <= 0) {
    return {
      result: "Not calculated",
      unit: "(Mode B)",
      formula: "Mode B: H_max = (MAASP − Safety_margin_psi) / (0.052·MW − G_influx) ;  Volume = H_max · annular_capacity",
      steps: [
        "Mode B — Simplified Planning Kick Tolerance Volume",
        `Validation: 0.052 × ${MW} = ${round(0.052 * MW, 4)} psi/ft is not greater than G_influx = ${Gi} psi/ft.`,
        "H_max not calculable — influx gradient is not lighter than mud column gradient. Verify influx-fluid type with the mud engineer / well-control engineer.",
        ...baseDisclaimers, modeBAssumption,
      ],
      outputs: {
        "Maximum allowable influx height (ft)": "H_max not calculable — influx gradient is not lighter than mud column gradient. Verify influx-fluid type with the mud engineer / well-control engineer.",
        "Estimated kick tolerance volume (bbl)": "Not calculated",
        "Safety margin used (psi)": S,
      },
      inputs: { "MAASP (psi)": P_maasp, "Current MW (ppg)": MW, "Influx Gradient (psi/ft)": Gi, "Shoe TVD (ft)": Dshoe, "Annular Capacity (bbl/ft)": Cap, "Safety Margin (psi)": S },
    };
  }

  const Hmax = (P_maasp - S) / denom;
  const Vol = Hmax * Cap;
  const geometryWarning = Hmax > Dshoe
    ? "Calculated influx height exceeds shoe TVD. Result is outside the simplified geometry limits and must be reviewed by competent well-control personnel."
    : null;

  return {
    result: round(Hmax, 0),
    unit: "ft (Mode B — H_max)",
    formula: "Mode B: H_max = (MAASP − Safety_margin_psi) / (0.052·MW − G_influx) ;  Volume = H_max · annular_capacity",
    steps: [
      "Mode B — Simplified Planning Kick Tolerance Volume",
      `Denominator = 0.052 × ${MW} − ${Gi} = ${round(denom, 4)} psi/ft`,
      `H_max = (${P_maasp} − ${S}) / ${round(denom, 4)} = ${round(Hmax, 0)} ft`,
      `Estimated kick tolerance volume = ${round(Hmax, 0)} × ${Cap} = ${round(Vol, 2)} bbl`,
      `Safety margin used = ${S} psi`,
      ...(geometryWarning ? [geometryWarning] : []),
      ...baseDisclaimers, modeBAssumption,
    ],
    outputs: {
      "Maximum allowable influx height (ft)": round(Hmax, 0),
      "Estimated kick tolerance volume (bbl)": round(Vol, 2),
      "Safety margin used (psi)": S,
      ...(geometryWarning ? { "Geometry note": geometryWarning } : {}),
    },
    inputs: { "MAASP (psi)": P_maasp, "Current MW (ppg)": MW, "Influx Gradient (psi/ft)": Gi, "Shoe TVD (ft)": Dshoe, "Annular Capacity (bbl/ft)": Cap, "Safety Margin (psi)": S },
  };
}
